import { useTranslation } from 'react-i18next';
import { Download, Moon, Search, Sparkles, Sun } from 'lucide-react';
import { HeaderNotifications } from '@/components/header/HeaderNotifications';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useTheme } from '@/contexts/ThemeContext';
import { useTenantHeaderNotifications } from '@/hooks/queries/useHeaderNotifications';
import { useChatUiStore } from '@/stores/chatUiStore';
import { useAppDownloadStore } from '@/stores/appDownloadStore';
import { useIsPwa } from '@/hooks/useIsPwa';
import { cn } from '@/lib/utils';

function openCommandPalette() {
  document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }));
}

interface FloatingTopActionsProps {
  /** Override positioning class. Default: `fixed top-4 right-4 z-20`. */
  className?: string;
}

/**
 * Floating top-right action cluster — replaces the legacy `ContextualTopBar`.
 *
 * Phase 8 iteration 3 — **user avatar removed** : the avatar with its dropdown
 * (Profile/Settings/Logout) now lives in the rail bottom slot (HeyGen pattern).
 * The top-right cluster is reserved for global ambient actions:
 * `sync · search · notifs · download · language · theme`.
 */
export function FloatingTopActions({ className }: FloatingTopActionsProps) {
  const { t: tCommon } = useTranslation('common');
  const notifications = useTenantHeaderNotifications();
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const openChat = useChatUiStore((s) => s.setOpen);
  const openDownloadModal = useAppDownloadStore((s) => s.openModal);

  return (
    <div
      role="toolbar"
      aria-label={tCommon('header_actions', { defaultValue: 'Actions globales' })}
      className={cn(
        'fixed top-4 right-4 z-20 hidden lg:flex',
        'items-center gap-1 p-1.5',
        'rounded-full border border-slate-200/80 bg-white/85 backdrop-blur-md',
        'shadow-[0_4px_16px_-2px_rgba(15,23,42,0.08),0_1px_2px_rgba(15,23,42,0.04)]',
        'supports-[backdrop-filter]:bg-white/70',
        'dark:border-charcoal-700/70 dark:bg-charcoal-900/85 dark:supports-[backdrop-filter]:bg-charcoal-900/70',
        className,
      )}
    >
      {/* Sync indicator — dot only (no label) */}
      <span
        role="status"
        aria-label={tCommon('header_sync_ok', { defaultValue: 'Synchro' })}
        title={tCommon('header_sync_ok', { defaultValue: 'Synchro' })}
        className="mx-1.5 size-1.5 animate-pulse rounded-full bg-lime-500"
      />

      {/* Search trigger */}
      <button
        type="button"
        onClick={openCommandPalette}
        aria-label={`${tCommon('search', { defaultValue: 'Rechercher' })} (⌘K)`}
        className="grid size-8 place-items-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-charcoal-950 dark:text-slate-400 dark:hover:bg-charcoal-800 dark:hover:text-slate-50"
      >
        <Search className="size-4" strokeWidth={1.75} />
      </button>

      {/* Notifications */}
      <HeaderNotifications
        items={notifications.items}
        unreadCount={notifications.unreadCount}
        isLoading={notifications.isLoading}
        onMarkAsRead={notifications.markAsRead}
        onMarkAllAsRead={notifications.markAllAsRead}
        className="size-8 rounded-full text-slate-500 hover:bg-slate-100 hover:text-charcoal-950 dark:text-slate-400 dark:hover:bg-charcoal-800 dark:hover:text-slate-50"
      />

      {/* App Download modal trigger */}
      <button
        type="button"
        onClick={openDownloadModal}
        aria-label={tCommon('download_app', { defaultValue: 'Télécharger les applications' })}
        title={tCommon('download_app', { defaultValue: 'Télécharger les applications' })}
        className="grid size-8 place-items-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-charcoal-950 dark:text-slate-400 dark:hover:bg-charcoal-800 dark:hover:text-slate-50"
      >
        <Download className="size-4" strokeWidth={1.75} />
      </button>

      {/* Language selector — Compact 1-click animated toggle */}
      <LanguageSwitcher variant="compact" size="md" />

      {/* Theme toggle */}
      <button
        type="button"
        onClick={() => setTheme(isDark ? 'light' : 'dark')}
        aria-label={tCommon(isDark ? 'theme_light' : 'theme_dark', {
          defaultValue: isDark ? 'Mode clair' : 'Mode sombre',
        })}
        className="grid size-8 place-items-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-charcoal-950 dark:text-slate-400 dark:hover:bg-charcoal-800 dark:hover:text-slate-50"
      >
        {isDark ? (
          <Sun className="size-4" strokeWidth={1.75} />
        ) : (
          <Moon className="size-4" strokeWidth={1.75} />
        )}
      </button>

      {/* Assistant IA — remplace le FAB « Demander à l'IA » sur desktop */}
      <button
        type="button"
        onClick={() => openChat(true)}
        aria-label={tCommon('ai_assistant', { defaultValue: 'Demander à l’IA' })}
        title={`${tCommon('ai_assistant', { defaultValue: 'Demander à l’IA' })} (⌘J)`}
        data-tour="ai-trigger"
        className="grid size-8 place-items-center rounded-full text-lime-600 transition-colors hover:bg-lime-50 hover:text-lime-700 dark:text-lime-400 dark:hover:bg-lime-400/10 dark:hover:text-lime-300"
      >
        <Sparkles className="size-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}

/**
 * Mobile variant — compact top-right cluster visible only on small screens.
 * The user avatar lives in the rail drawer (open via the top-left hamburger).
 */
export function FloatingTopActionsMobile({ className }: FloatingTopActionsProps) {
  const { t: tCommon } = useTranslation('common');
  const notifications = useTenantHeaderNotifications();
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const openDownloadModal = useAppDownloadStore((s) => s.openModal);
  const isPwa = useIsPwa();

  return (
    <div
      role="toolbar"
      aria-label={tCommon('header_actions', { defaultValue: 'Actions globales' })}
      className={cn(
        'fixed top-[calc(env(safe-area-inset-top,0px)+0.75rem)] right-3 z-20 flex lg:hidden',
        'items-center gap-1 p-1',
        'rounded-full border border-slate-200/80 bg-white/90 backdrop-blur-md',
        'shadow-[0_4px_16px_-2px_rgba(15,23,42,0.08)]',
        'dark:border-charcoal-700/70 dark:bg-charcoal-900/90',
        className,
      )}
    >
      <span
        role="status"
        aria-label={tCommon('header_sync_ok', { defaultValue: 'Sync OK' })}
        className="mx-1 size-1.5 animate-pulse rounded-full bg-lime-500"
      />
      <button
        type="button"
        onClick={openCommandPalette}
        aria-label={tCommon('search', { defaultValue: 'Rechercher' })}
        className="grid size-8 place-items-center rounded-full text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-charcoal-800"
      >
        <Search className="size-4" strokeWidth={1.75} />
      </button>
      <HeaderNotifications
        items={notifications.items}
        unreadCount={notifications.unreadCount}
        isLoading={notifications.isLoading}
        onMarkAsRead={notifications.markAsRead}
        onMarkAllAsRead={notifications.markAllAsRead}
        className="size-8 rounded-full text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-charcoal-800"
      />
      {!isPwa && (
        <button
          type="button"
          onClick={openDownloadModal}
          aria-label={tCommon('download_app', { defaultValue: 'Télécharger les applications' })}
          title={tCommon('download_app', { defaultValue: 'Télécharger les applications' })}
          className="grid size-8 place-items-center rounded-full text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-charcoal-800"
        >
          <Download className="size-4" strokeWidth={1.75} />
        </button>
      )}
      <LanguageSwitcher variant="compact" size="sm" />
      <button
        type="button"
        onClick={() => setTheme(isDark ? 'light' : 'dark')}
        aria-label={tCommon(isDark ? 'theme_light' : 'theme_dark', {
          defaultValue: isDark ? 'Mode clair' : 'Mode sombre',
        })}
        className="grid size-8 place-items-center rounded-full text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-charcoal-800"
      >
        {isDark ? (
          <Sun className="size-4" strokeWidth={1.75} />
        ) : (
          <Moon className="size-4" strokeWidth={1.75} />
        )}
      </button>
    </div>
  );
}
